import React from "react";
import {
  CButton,
  CCol,
  CFormGroup,
  CInputGroup,
  CInputGroupAppend,
  CLabel,
  CRow,
} from "@coreui/react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { getFieldErrors } from "../../../models/errors";
import EstablishmentSelect from "../../establishments/EstablishmentSelect";
import DispatchPointSelect from "../../dispatch-point/DispatchPointSelect";
import StampSelect from "../../stamps/StampSelect";
import ClientSelect from "../../client/ClientSelect";
import { FieldErrors } from "../../form/FieldErrors";
import ClientFieldErrors from "../../form/ClientFieldErrors";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { useInvoiceFormContext } from "../context/InvoiceFormContext";

interface InvoiceFormHeaderProps {
  onAddClientClick: () => void;
}

const sectionDividerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  marginBottom: "16px",
  marginTop: "8px",
};

const sectionBarStyle: React.CSSProperties = {
  width: "4px",
  height: "18px",
  background: "#006657",
  borderRadius: "2px",
  marginRight: "10px",
  flexShrink: 0,
};

const sectionLineStyle: React.CSSProperties = {
  flex: 1,
  height: "1px",
  background: "var(--theme-divider)",
  marginLeft: "10px",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontWeight: 600,
  color: "var(--theme-muted)",
  fontSize: "0.78rem",
  textTransform: "uppercase",
  letterSpacing: "0.8px",
};

const SectionDivider: React.FC<{ icon: string; label: string }> = ({
  icon,
  label,
}) => (
  <div style={sectionDividerStyle}>
    <div style={sectionBarStyle} />
    <i
      className={`fa ${icon} text-primary mr-2`}
      style={{ fontSize: "0.85rem" }}
    ></i>
    <h6 style={sectionTitleStyle}>{label}</h6>
    <div style={sectionLineStyle} />
  </div>
);

const FormLabel: React.FC<{
  required?: boolean;
  children: React.ReactNode;
}> = ({ required, children }) => (
  <CLabel
    className="mb-0"
    style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}
  >
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

export const InvoiceFormHeader: React.FC<InvoiceFormHeaderProps> = ({
  onAddClientClick,
}) => {
  const {
    stamp,
    establishment,
    dispatchPoint,
    client,
    invoiceDate,
    onStampChange,
    onEstablishmentChange,
    onDispatchPointChange,
    onClientChange,
    onInvoiceDateChange,
    errors,
  } = useInvoiceFormContext();

  const stamps = useSelector(
    (state: RootState) => state.taxPayerParameters.data.stamps,
  );
  const establishments = useSelector(
    (state: RootState) => state.taxPayerParameters.data.establishments,
  );
  const dispatchPoints = useSelector(
    (state: RootState) => state.taxPayerParameters.data.dispatchPoints,
  );

  const isStampDisabled = stamps.length === 1;
  const isEstablishmentDisabled = establishments.length === 1;
  const isDispatchPointDisabled = dispatchPoints.length === 1;

  const handleEstablishmentChange = (est: typeof establishment) => {
    onEstablishmentChange(est);
    onDispatchPointChange(null);
  };
  return (
    <>
      <SectionDivider icon="fa-info-circle" label="Datos Generales" />
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Timbrado:</FormLabel>
          </CCol>
          <CCol>
            <fieldset disabled={isStampDisabled}>
              <StampSelect onChange={onStampChange} value={stamp} />
            </fieldset>
            <FieldErrors
              errors={getFieldErrors("stampId", errors) as string[]}
            ></FieldErrors>
            <FieldErrors
              errors={getFieldErrors("stampIdentifier", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Establecimiento:</FormLabel>
          </CCol>
          <CCol>
            <fieldset disabled={isEstablishmentDisabled}>
              <EstablishmentSelect
                onChange={handleEstablishmentChange}
                value={establishment}
              />
            </fieldset>
            <FieldErrors
              errors={getFieldErrors("establishmentId", errors) as string[]}
            ></FieldErrors>
            <FieldErrors
              errors={getFieldErrors("establishmentCode", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Punto de Expedición:</FormLabel>
          </CCol>
          <CCol>
            <fieldset disabled={isDispatchPointDisabled}>
              <DispatchPointSelect
                key={establishment?.id ?? 0}
                onChange={onDispatchPointChange}
                value={dispatchPoint}
                establishmentId={establishment?.id}
              />
            </fieldset>
            <FieldErrors
              errors={getFieldErrors("dispatchPointId", errors) as string[]}
            ></FieldErrors>
            <FieldErrors
              errors={
                getFieldErrors("dispatchPointIdentifier", errors) as string[]
              }
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Cliente:</FormLabel>
          </CCol>
          <CCol>
            <CInputGroup>
              <ClientSelect
                editable
                value={client}
                onChange={onClientChange}
              ></ClientSelect>
              <CInputGroupAppend>
                <CButton
                  className="btn btn-primary"
                  style={{ zIndex: 0 }}
                  onClick={onAddClientClick}
                  title="Agregar nuevo cliente"
                >
                  <i className="fa fa-user-plus"></i>
                </CButton>
              </CInputGroupAppend>
            </CInputGroup>
            <ClientFieldErrors errors={errors} />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Fecha de emisión:</FormLabel>
          </CCol>
          <CCol>
            <Datetime
              className="pl-0"
              onChange={onInvoiceDateChange}
              value={invoiceDate}
              locale="es/PY"
              dateFormat="DD/MM/YYYY"
              timeFormat="HH:mm"
              closeOnSelect={true}
            ></Datetime>
            <FieldErrors
              errors={getFieldErrors("invoiceDate", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
    </>
  );
};
